import {makeAutoObservable, observable, reaction, runInAction, flow, flowResult} from "mobx";
import {ViewController} from "data/types/structure";
import {injectable, inject} from "inversify";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import {Countdown, wait, extractErrorMessage, trackSentryErrors} from "data/utils";
import {RequestState, ModalType, ContestStatus, PlayerGamePosition} from "data/enums";
import {Bindings} from "data/constants/bindings";
import type {IModalsStore} from "data/stores/modals/modals.store";
import type {IPlayersStore} from "data/stores/players/players.store";
import type {IRosterStore} from "data/stores/roster/roster.store";
import type {IContestsStore} from "data/stores/contests/contestsStore";
import type {IContestPlayerStatsStore} from "data/stores/contest_player_stats/contest_player_stats.store";
import {compact, take, orderBy} from "lodash";
import {AxiosError} from "axios/index";
import {getPlayerAvatar} from "data/utils/player";
import {IApiResponse} from "data/services/http";
import type {IGamePlayer, IPlayerCalculations} from "data/utils/player.calculations";
import type {IContextualPromptBannerStore} from "data/stores/contextual_prompt_banner/contextual_prompt_banner.store";
import type {IUserStore} from "data/stores/user/user.store";

export interface IWheelViewController extends ViewController {
	i18n: ILocalizationStore;

	get contestId(): number;

	get players(): IGamePlayer[];

	get isLocked(): boolean;

	get isLockedScreen(): boolean;

	get spinsLeft(): number;

	get prizeObject(): IGamePlayer | null;

	get secondsLeft(): number;

	get isPitcherSpin(): boolean;

	get isLimitReached(): boolean;

	get selectedPlayer(): number;

	get isPlayerLoading(): boolean;

	onSpin: () => void;
	addPlayer: () => void;
	clearSpin: () => void;
	selectPlayer: (playerId: number) => void;
	clearSpinWithPlayers: () => void;
}

@injectable()
export class WheelViewController implements IWheelViewController {
	private _cancelableSpinFlow: ReturnType<typeof flowResult<Generator>> | null = null;
	private SPINS_LIMIT = 9;
	private _timer: Countdown;
	@observable private _prizeId: number | null = null;
	@observable private _playerID = 0;
	@observable private _secondsLeft = 0;
	@observable private _spinState: RequestState = RequestState.IDLE;
	_fetchDataDisposer: ReturnType<typeof reaction> | null = null;

	constructor(
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore,
		@inject(Bindings.ModalsStore) private readonly _modalsStore: IModalsStore,
		@inject(Bindings.PlayersStore) private readonly _playersStore: IPlayersStore,
		@inject(Bindings.RosterStore) private readonly _rosterStore: IRosterStore,
		@inject(Bindings.ContestsStore) private readonly _contestStore: IContestsStore,
		@inject(Bindings.UserStore) private readonly _userStore: IUserStore,
		@inject(Bindings.ContextualPromptBannerStore)
		private readonly _contextualPromptStore: IContextualPromptBannerStore,
		@inject(Bindings.PlayerCalculations)
		private readonly _playerCalculations: IPlayerCalculations,
		@inject(Bindings.ContestPlayerStatsStore)
		private readonly _contestStatsStore: IContestPlayerStatsStore
	) {
		this._timer = new Countdown(new Date());
		makeAutoObservable(this);
	}

	get contestId(): number {
		return this.contest?.id || 0;
	}

	get currentSpinnerIds() {
		return this._rosterStore.currentSpinnerIds;
	}
	get wheelPlayerIds() {
		return this.currentSpinnerIds.length ? this.currentSpinnerIds : this.playersNoSelection;
	}

	get players() {
		const playerIds = this.wheelPlayerIds;
		const contest = this.contest;
		if (playerIds.length && contest) {
			return compact(
				playerIds.map((playerId) => {
					return this.getGamePlayer(playerId, contest.id);
				})
			);
		}

		return [];
	}

	private getGamePlayer(playerId: number, contestId: number) {
		return this._playerCalculations.getGamePlayerById(playerId, contestId);
	}

	get isLocked(): boolean {
		return (
			this.contest?.status === ContestStatus.Complete ||
			!this._rosterStore.currentSpinnerIds.length
		);
	}

	get isLockedScreen(): boolean {
		return this._rosterStore.isLockScreen;
	}

	get spinsLeft() {
		return this.SPINS_LIMIT - this._rosterStore.spinsCount;
	}

	get prizeObject() {
		return this.players.find((it) => it.id === this._prizeId) || null;
	}

	get secondsLeft() {
		return this._secondsLeft;
	}

	get isPitcherSpin(): boolean {
		const roster = this.roster;
		return roster?.players.length === 1;
	}

	get isLimitReached(): boolean {
		return this._rosterStore.isLimitReached;
	}

	get selectedPlayer() {
		return this._playerID;
	}

	get isPlayerLoading() {
		return this._spinState === RequestState.PENDING;
	}

	// Method to start the spinning process
	onSpin = async () => {
		if (this._prizeId) {
			return;
		}

		this._spinState = RequestState.PENDING;
		this._timer.clear();

		try {
			const response = await this._rosterStore.saveSpin();
			const playerId = response?.playerId;
			if (playerId) {
				this._cancelableSpinFlow = flowResult(this._startCancelableSpinFlow(playerId));
				this._cancelableSpinFlow.catch((reason) => {
					const error = reason as Error;
					if (error.message === "FLOW_CANCELLED") {
						//ignore the error as it's expected
					}
				});
			}
		} catch (err) {
			this._spinState = RequestState.ERROR;
			const e = err as AxiosError<IApiResponse>;
			this.onError(e);
		}
	};

	@flow
	private *_startCancelableSpinFlow(playerId: number) {
		if (!this._rosterStore.currentSpinnerIds.includes(playerId)) {
			this._modalsStore.showModal(ModalType.ERROR, {
				message: `Player is not on the wheel, playerdId: ${playerId}, allowed players ${this._rosterStore.currentSpinnerIds.join(
					", "
				)}`,
			});
			this._spinState = RequestState.ERROR;
			return;
		}

		this._prizeId = playerId;
		yield wait(5500);

		this._spinState = RequestState.SUCCESS;

		yield wait(500);

		this.fetchPlayerImages();

		if (this.isLimitReached) {
			this._rosterStore.updateLineupPlayers();
			yield wait(4000);
			if (this._rosterStore.isRosterFull) {
				this._rosterStore.updateView(false);
			}
			this.clearSpinWithPlayers();
		} else {
			this.startTimer();
		}
	}

	addPlayer = () => {
		this.clearSpinWithPlayers();
		void this._rosterStore
			.addPlayer()
			.then(() => {
				if (this._rosterStore.isRosterFull) {
					this._rosterStore.updateView(false);
					this.checkContextualPromptBanner();
				}
			})
			.catch(this.onError);
	};

	clearSpin = () => {
		this._cancelableSpinFlow?.cancel();
		this._prizeId = null;
		this._timer.stop();
		this._timer.clear();
		this._secondsLeft = 0;
		this._rosterStore.removeLockScreen();
	};

	selectPlayer = (playerId: number) => {
		this._playerID = playerId;
	};

	clearSpinWithPlayers = () => {
		this._rosterStore.updatePlayers();
		this._rosterStore.removeLockScreen();
		this.clearSpin();
	};

	dispose() {
		this._cancelableSpinFlow?.cancel();
		this._fetchDataDisposer?.();
	}

	private get contest() {
		return this._contestStore.getContestById(this._rosterStore.currentContestId);
	}

	private get roster() {
		return this._rosterStore.currentRoster;
	}

	private get playersNoSelection() {
		const contestId = this.contest?.id;
		if (!contestId) {
			return [];
		}

		const playersWithProjPoints =
			this._contestStatsStore.stats?.filter((playerStats) => {
				if (playerStats.isRemoved) {
					return false;
				}
				const player = this._playersStore.listById[playerStats.playerId];
				return player.gamePosition === PlayerGamePosition.Hitter;
			}) ?? [];

		const sortedPlayers = orderBy(
			playersWithProjPoints,
			[
				(player) => {
					return player?.rank ?? -1;
				},
				(player) => {
					return player.projectedPoints ?? -1;
				},
				(player) => {
					return this._playersStore
						.getPlayerById(player.playerId)
						?.lastName.toLowerCase();
				},
			],
			["asc", "desc", "asc"]
		);

		return take(sortedPlayers, 8).map((it) => it.playerId);
	}

	private startTimer() {
		this._timer
			.updateDate(new Date(Date.now() + 10999))
			.onTick((_days, _hours, _minutes, seconds) => {
				runInAction(() => {
					this._secondsLeft = seconds;
				});
			})
			.onComplete(() => {
				this.addPlayer();
			})
			.run();
	}

	private onError = (e: AxiosError<IApiResponse>) => {
		this._modalsStore.showModal(ModalType.ERROR, {
			message: extractErrorMessage(e),
		});
		trackSentryErrors(e, {}, "wheel.spin");
	};

	private fetchPlayerImages() {
		const playerIds = this._rosterStore.beforeSpinPlayerIds;
		playerIds.forEach((it) => {
			const player = this._playersStore.getPlayerById(it);
			if (player) {
				void getPlayerAvatar(player);
			}
		});
	}

	private checkContextualPromptBanner = () => {
		void this._contextualPromptStore.check(this._userStore.uid);
	};
}
